import {LocalizedText} from "crqlar-react-common/dist/models/common/localizedText.model";
import {Survey} from "crqlar-react-common/dist/models/survey/survey.model";
import {SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import {type PickerVariable, resolveVariable, variableTokenPattern} from "../../common/newVariablePicker";
import {SURVEY_VARIABLES} from "./surveyVariables";

/** Each survey variable as far as resolution reads one: its kind and its sample. */
const PREVIEW_VARIABLES = new Map<string, PickerVariable>(SURVEY_VARIABLES.map(variable => [variable.key, {
    key: variable.key,
    label: variable.labelKey,
    description: '',
    scopeKey: variable.scope,
    scope: variable.scope,
    kind: variable.kind,
    resolve: () => variable.sample,
}]));

/*
    A token's own fallback still wins, as it always has here: a template writes its fallbacks in the
    text's language ("Hallo {{guestFirstName|zusammen}}"), while a text sample stays English. Without
    one, the sample is formatted as the API formats the real value — in the text's language and the
    token's format, where an unknown or empty format is the kind's default. An unknown key with no
    fallback shows nothing.
 */
const resolveVariablePreviewText = (text: string, language: string): string => (
    text.replace(variableTokenPattern(), (_token: string, key: string, format?: string, fallback?: string) => {
        if (fallback) return fallback;

        const variable = PREVIEW_VARIABLES.get(key);
        return variable ? resolveVariable(variable, {language, format}) : '';
    })
);

const resolveLocalized = (text?: LocalizedText): LocalizedText | undefined => {
    if (!text) return text;

    const resolved: LocalizedText = {};
    Object.entries(text).forEach(([language, value]) => {
        resolved[language] = typeof value === 'string' ? resolveVariablePreviewText(value, language) : value;
    });
    return resolved;
};

const resolveQuestion = (question: SurveyQuestion): SurveyQuestion => ({
    ...question,
    title: resolveLocalized(question.title),
    description: resolveLocalized(question.description),
    helpText: resolveLocalized(question.helpText),
    sectionTitle: resolveLocalized(question.sectionTitle),
    placeholder: resolveLocalized(question.placeholder),
    lowLabel: resolveLocalized(question.lowLabel),
    highLabel: resolveLocalized(question.highLabel),
    yesLabel: resolveLocalized(question.yesLabel),
    noLabel: resolveLocalized(question.noLabel),
    termsText: resolveLocalized(question.termsText),
    agreementLabel: resolveLocalized(question.agreementLabel),
    buttonLabel: resolveLocalized(question.buttonLabel),
    options: question.options?.map(option => ({...option, label: resolveLocalized(option.label)})),
});

export const resolveSurveyForPreview = <T extends Pick<Survey, 'questions'>>(survey: T): T => ({
    ...survey,
    questions: survey.questions?.map(resolveQuestion),
});
